import { useEffect, useState } from 'react';
import { api, ApiError, type InvitationPreview } from '../api';
import type { Dictionary } from '../i18n';
import { Error as ErrorIcon, Lock, Mark } from '../components/Icons';

/**
 * Ekran z linku w zaproszeniu (projekt „Start, logowanie, rejestracja”, ekran 07).
 *
 * Podgląd działa bez logowania — zapraszany może dopiero zakładać konto. Przyjąć zaproszenie
 * może wyłącznie osoba zalogowana na wskazany adres; pilnuje tego API, nie ten ekran.
 *
 * Zakres roli pokazujemy wprost przed przyjęciem, bo zaproszenie do cudzych ksiąg to nie jest
 * decyzja, którą podejmuje się w ciemno.
 */
export function InvitationScreen({
  token,
  t,
  signedIn,
  onSignIn,
  onAccepted,
}: {
  token: string;
  t: Dictionary;
  signedIn: boolean;
  onSignIn: () => void;
  onAccepted: () => void;
}) {
  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    api
      .invitationPreview(token)
      .then(setPreview)
      .catch((failure: unknown) => setError(failure instanceof ApiError ? failure.message : String(failure)))
      .finally(() => setBusy(false));
  }, [token]);

  async function accept() {
    setBusy(true);
    setError(null);
    try {
      await api.acceptInvitation(token);
      onAccepted();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="frame solo">
      <div className="pane center">
        <div className="brand" style={{ color: 'var(--color-brand-primary)' }}>
          <Mark />
          <b>{t.appName}</b>
        </div>

        <div className="hd">
          <h3>{t.invitation.title}</h3>
          {preview && <p>{t.invitation.lead(preview.invitedByName)}</p>}
        </div>

        {busy && <p className="muted">{t.loading}</p>}
        {error && (
          <div className="alert a-bad">
            <ErrorIcon />
            <div>{error}</div>
          </div>
        )}

        {preview && (
          <>
            <div className="invite">
              <span className="av">{initials(preview.invitedByName)}</span>
              <span className="tx">
                <b>{preview.invitedByName}</b>
                <span>
                  {t.invitation.invitesAs} <b>{t.access.roles[preview.role]}</b>
                </span>
                <span>
                  {t.invitation.company}: {preview.companyName}
                </span>
              </span>
            </div>

            <div className="alert a-info">
              <Lock />
              <div>
                <b>{t.access.roles[preview.role]}</b>
                <p>{t.invitation.roleScope[preview.role]}</p>
              </div>
            </div>

            <div className="entry-form">
              <p className="hint">
                {t.invitation.forEmail}: {preview.email}
              </p>
              {signedIn ? (
                <button type="button" className="btn wide" onClick={() => void accept()} disabled={busy}>
                  {t.invitation.accept}
                </button>
              ) : (
                <button type="button" className="btn wide" onClick={onSignIn}>
                  {t.invitation.signInToAccept}
                </button>
              )}
            </div>

            <p className="hint">
              {t.invitation.expires(new Date(preview.expiresAt).toLocaleDateString())}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/** Inicjały zapraszającego — obrazka nie mamy i mieć nie musimy. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}
